/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import authService from '@/services/auth-service';
import type { UserEntity, UpdateUserProfileApiRequest } from '@trackv16/types';
import i18n from '@/lib/i18n';
import { queryClient } from '@/providers/query-provider';
import { profileKeys } from '@/features/profile/hooks/use-profile';

interface AuthState {
  user: UserEntity | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  setUser: (user: UserEntity) => void;
  checkAuth: () => Promise<void>;
  updateProfile: (data: UpdateUserProfileApiRequest) => Promise<void>;
  syncUserLanguage: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Update React Query cache with the user data from login
          queryClient.setQueryData(profileKeys.current(), response.user);
        } catch (error: any) {
          // Check if it's an email not verified error
          if (error?.response?.data?.error === 'EMAIL_NOT_VERIFIED') {
            set({
              isLoading: false,
              error: 'Email not verified',
            });
            // Re-throw with additional info
            const enhancedError = new Error('EMAIL_NOT_VERIFIED');
            (enhancedError as any).email = error.response?.data?.email || email;
            (enhancedError as any).response = error.response;
            throw enhancedError;
          }

          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          // Clear TanStack Query cache on logout
          queryClient.clear();
        }
      },

      setUser: (user: UserEntity) => {
        set({ user, isAuthenticated: true });

        // Update TanStack Query profile cache when user is set
        queryClient.setQueryData(profileKeys.current(), user);
      },

      checkAuth: async () => {
        // Check if we have tokens (either access in memory or refresh in storage)
        const hasAccess = authService.isAuthenticated();
        const hasRefresh = await authService.getRefreshToken();

        if (!hasAccess && !hasRefresh) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        // If we have refresh but no access, try to refresh
        if (!hasAccess && hasRefresh) {
          try {
            await authService.refreshToken();
            set({ isAuthenticated: true });
          } catch {
            set({ isAuthenticated: false, user: null });
            return;
          }
        } else {
          set({ isAuthenticated: true });
        }

        // Try to get cached user data from React Query without triggering a fetch
        const cachedUser = queryClient.getQueryData(profileKeys.current()) as
          | UserEntity
          | undefined;
        if (cachedUser) {
          set({ user: cachedUser });
        }
      },

      updateProfile: async (data: UpdateUserProfileApiRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedUser = await authService.updateProfile(data);
          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });

          // Update TanStack Query profile cache to ensure UI reflects changes
          queryClient.setQueryData(profileKeys.current(), updatedUser);

          // If language was updated, sync it with i18n immediately
          if (data.language && data.language !== i18n.language) {
            await i18n.changeLanguage(data.language);
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Profile update failed',
          });
          throw error;
        }
      },

      syncUserLanguage: async () => {
        const state = useAuthStore.getState();
        if (state.isAuthenticated && state.user?.language) {
          const currentLanguage = i18n.language;
          const userLanguage = state.user.language;

          if (currentLanguage !== userLanguage) {
            console.log(
              `Syncing language from ${currentLanguage} to ${userLanguage}`
            );
            await i18n.changeLanguage(userLanguage);
          }
        }
      },

      deleteAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          await authService.deleteAccount();

          // Clear all user data
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          // Clear all caches
          queryClient.clear();
        } catch (error: any) {
          set({
            isLoading: false,
            error:
              error instanceof Error
                ? error.message
                : 'Failed to delete account',
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
